import React, { useMemo, useState, useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Container, Alert, Button, Snackbar, Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectAuth } from 'services/auth/redux/selectors'
import { getAuthPermissions, PERM_UPDATE_CONTENT } from 'services/auth'
import { getIdFromLocation } from 'services/content'
import { selectLocation } from 'services/location/redux/selectors'
import { LANGUAGE_EN } from 'services/languages/constants'
import Loader from 'gui/components/common/Loader'
import CourseModuleContentInformationSection from './CourseModuleContentInformationSection'
import CourseModuleContentSection from './course-module-content-section'
import { GET_COURSE_MODULE_CONTENT, UPDATE_COURSE_CONTENT } from './queries'
import type { ContentInfoChanges, SnackbarState } from './types'
import './course-detail.scss'

/**
 * Course Module Detail Component
 * Displays and allows editing of Course Module content
 *
 * This component uses Apollo Client for data fetching (modern YI pattern)
 * No Redux for content data - only for auth and location
 */
function CourseModuleDetail () {
  const auth = useSelector(selectAuth)
  const location = useSelector(selectLocation)
  const authPermissions = getAuthPermissions(auth)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)

  // Get content ID from URL
  const id = getIdFromLocation(location)

  // Fetch course module data from GraphQL
  const { loading, error, data } = useQuery(GET_COURSE_MODULE_CONTENT, {
    variables: { id },
    skip: !id,
  })

  // Get data from GraphQL response as plain object
  const moduleData = useMemo(() => data?.contentList?.content?.[0] || null, [data])

  // Extract language for content references (passed to child component)
  const moduleLanguage = useMemo(() => moduleData?.language || LANGUAGE_EN, [moduleData])

  // Track dirty state from Content Information section
  const [contentInfoDirty, setContentInfoDirty] = useState<boolean>(false)

  // Store changed values from Content Information section
  const [contentInfoChanges, setContentInfoChanges] = useState<ContentInfoChanges | null>(null)

  // Track dirty state from Module Content section
  const [moduleContentDirty, setModuleContentDirty] = useState<boolean>(false)

  // Store the save handler from Module Content section
  const [moduleContentSaveHandler, setModuleContentSaveHandler] = useState<
    (() => Promise<void>) | null
  >(null)

  // Track reset trigger counter
  const [resetTrigger, setResetTrigger] = useState<number>(0)

  // Overall dirty state (includes both content info and course module content)
  const isDirty = contentInfoDirty || moduleContentDirty

  // Handle dirty change from Content Information
  const handleContentInfoDirtyChange = useCallback((isContentDirty: boolean) => {
    setContentInfoDirty(isContentDirty)
  }, [])

  // Handle changes update from Content Information
  const handleContentInfoChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setContentInfoChanges(changes)
  }, [])

  // Handle dirty change from Module Content section
  const handleModuleContentDirtyChange = useCallback((isModuleContentDirty: boolean) => {
    setModuleContentDirty(isModuleContentDirty)
  }, [])

  // Handle save from Module Content section
  const handleModuleContentSave = useCallback((saveHandler: () => Promise<void>) => {
    setModuleContentSaveHandler(() => saveHandler)
  }, [])

  // Mutation for updating course module content
  const [updateModuleContent, { loading: saving }] = useMutation(UPDATE_COURSE_CONTENT, {
    refetchQueries: [{ query: GET_COURSE_MODULE_CONTENT, variables: { id } }],
    awaitRefetchQueries: true,
  })

  // Snackbar state for success/error messages
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Handle save
  const handleSave = async () => {
    try {
      const hasContentChanges = !!contentInfoChanges
      const hasModuleContentChanges = moduleContentDirty && moduleContentSaveHandler

      // Check if there are changes to save
      if (!hasContentChanges && !hasModuleContentChanges) {
        setSnackbar({
          open: true,
          message: 'No changes to save',
          severity: 'info',
        })
        return
      }

      // Save content information changes if any
      if (hasContentChanges) {
        await updateModuleContent({
          variables: {
            content: contentInfoChanges.contentInput,
            generatePath: contentInfoChanges.generatePath,
          },
        })
      }

      // Save course module content changes if any
      if (hasModuleContentChanges && moduleContentSaveHandler) {
        await moduleContentSaveHandler()
      }

      // Show success message
      setSnackbar({
        open: true,
        message: 'Course Module content saved successfully!',
        severity: 'success',
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setSnackbar({
        open: true,
        message: `Error saving content: ${errorMessage}`,
        severity: 'error',
      })
    }
  }

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  // Handle cancel - trigger reset in child
  const handleCancel = () => {
    setResetTrigger((prev) => prev + 1)
    setSnackbar({
      open: true,
      message: 'Changes discarded',
      severity: 'info',
    })
  }

  // Loading state
  if (loading) {
    return <Loader />
  }

  // Error state
  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 3 }}>
          <div>
            <strong>Error loading Course Module</strong>
          </div>
          <div>{error.message}</div>
        </Alert>
      </Container>
    )
  }

  // Not found state
  if (!moduleData && !loading) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 3 }}>
          <div>
            <strong>Course Module not found</strong>
          </div>
          <div>No Course Module found with ID: {id}</div>
        </Alert>
      </Container>
    )
  }

  return (
    <Container className="course-module-detail content-detail" maxWidth={false}>
      {/* Content Information Section */}
      <Container className="content-detail__section content-information" maxWidth={false}>
        <CourseModuleContentInformationSection
          contentData={moduleData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={handleContentInfoDirtyChange}
          onChangesUpdate={handleContentInfoChangesUpdate}
          onResetTriggered={resetTrigger}
        />
      </Container>

      {/* Course Module Content Section */}
      <Container className="content-detail__section course-module-content" maxWidth={false}>
        <CourseModuleContentSection
          moduleId={id!}
          moduleContentId={moduleData?.contentId}
          moduleLanguage={moduleLanguage}
          disabled={isViewOnlyMode}
          onDirtyChange={handleModuleContentDirtyChange}
          onSaveHandler={handleModuleContentSave}
          onResetTriggered={resetTrigger}
        />
      </Container>

      {/* Hovering Save and Cancel Buttons - Only appear when there are changes */}
      {!isViewOnlyMode && (
        <Box
          className="content-detail__action-buttons"
          sx={{
            position: 'fixed',
            bottom: isDirty ? '20px' : '-100px',
            right: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.3s ease',
            opacity: isDirty ? 1 : 0,
            pointerEvents: isDirty ? 'auto' : 'none',
            minWidth: '200px',
          }}
        >
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              sx={{
                flex: 1,
                minWidth: '100px',
                height: '42px',
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
              sx={{
                flex: 1,
                minWidth: '100px',
                height: '42px',
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default CourseModuleDetail
